import { getAssetAvailableTypeByExtension } from "~/js/network/mime-type-handler";
import EventAnalytics, { setSelectedTemplateId } from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { WorkspaceApi } from "~/solidJs/entities/WorkspaceListEntity/api/WorkspaceApi";
import type { IWorkspace } from "~/solidJs/entities/WorkspaceListEntity/helpers/workspaceTypes";
import { getWorkspacesStore } from "~/solidJs/entities/WorkspaceListEntity/models/wrokspaceListContext";
import { type DownloadLinkResponse, FileApi } from "~/solidJs/shared/api/fileApi";
import {
	getIsSaveFunctionalityAvailable,
	getLinksAndMap,
	getPrivateMessageFromError,
	type IFileType
} from "~/solidJs/shared/helpers";
import type {
	IUserTemplateParams,
	IWorkspaceFileSocket
} from "~/solidJs/shared/helpers/types/INodeDefinition";
import { isSomeWorkspaceOppend } from "~/solidJs/shared/helpers/workspaceHelpers";
import { setIsSaveNewProjectVisible } from "~/solidJs/shared/models/globalStores";
import { openPricingPage } from "~/solidJs/shared/models/pageParams/pricingPageParams";
import {
	showToastMessage,
	ToastMessageTypes
} from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";
import Editor from "./editor";
import { getDefinitionOrAwait } from "./graph/node-library";
import { Connection, type GraphNode } from "./graph/node-socket-connection";
import type { IMlBaseItem } from "./network/airtable-ml-base";
import { pullAssetViaAws } from "./network/asset-storage";
import NodeExecutionTask from "./network/node-task";
import { loadCommentFrameAccordType } from "./nodes/local/logic/commentFrame";
import { type NodeDefinition, SocketDefinition } from "./nodes/node-definition";
import { $modalView, hideAllModals } from "./utilities/TurboDom";

function addSavedNode(baseItem: IMlBaseItem | NodeDefinition) {
	let node: NodeDefinition;
	if ("node" in baseItem) {
		node = baseItem.node!;
	} else {
		node = baseItem as NodeDefinition;
	}
	const nodeInWorkspace = Editor.context.graph.addNode(
		node,
		screen.width / 3,
		screen.height / 3,
		false,
		false,
		false,
		true
	);
	nodeInWorkspace.loader.show();
}

export async function clearWorkspace() {
	if (!Editor.context.graph) return;
	setSelectedTemplateId(undefined);
	NodeExecutionTask.stopTasksAsync(Editor.context.graph.nodes);
	for (const node of [...Editor.context.graph.nodes]) {
		await Editor.context.graph.deleteNode(node, true);
	}
	for (const key of [...Editor.context.graph.commentFrameMap.keys()]) {
		Editor.context.graph.deleteCommentFrame({ id: key });
	}
}

export async function collectNodesData(fixedSeed = true) {
	try {
		const userNodes: IUserTemplateParams[] = [];

		for (const node of Editor.context.graph.nodes) {
			try {
				userNodes.push(await node.serializeNodeState(true, true, false, fixedSeed));
			} catch (e) {
				EventAnalytics.sendErrorInfo("collect-node-data-error", {
					message: getPrivateMessageFromError(e) || "collect-nodes-data-fc-error",
					globalId: node.globalId
				});
				throw e;
			}
		}

		return userNodes;
	} catch (e) {
		console.log(e);
		EventAnalytics.sendErrorInfo("collect-nodes-data-fc-error", {
			message: getPrivateMessageFromError(e) || "collect-nodes-data-fc-error"
		});
		throw e;
	}
}

/**
 *
 * @param projectConfig
 */
export function getArrayOfFilesIdsInProject(projectConfig: IWorkspace) {
	const fileIdSet = new Set<number>();
	for (const node of projectConfig.nodes) {
		for (const outputSocket of node.outputSocketGroup) {
			if (outputSocket.type === "text") continue;
			if (!Array.isArray(outputSocket.value)) continue;
			for (const value of outputSocket.value) {
				if (Array.isArray(value)) {
					for (const fileValue of value) {
						if (!fileValue.fileId) continue;
						fileIdSet.add(fileValue.fileId);
					}
					continue;
				}
				if (!value.fileId) continue;
				fileIdSet.add(value.fileId);
			}
		}
	}

	for (const sticker of Object.values(projectConfig.stickers)) {
		if (!sticker.fileId) continue;
		fileIdSet.add(sticker.fileId);
	}

	return Array.from(fileIdSet);
}

export async function collectWorkspaceData(fixedSeed = true) {
	try {
		const userNodes = await collectNodesData(fixedSeed);

		const jsonData: IWorkspace = {
			nodes: userNodes,
			editor: {
				position: Editor.context.graph.navigation.getPosition(),
				zoomScale: Editor.context.graph.navigation.getZoomScale()
			},
			stickers: {}
		};

		const stickersInGraph = Object.fromEntries(Editor.context.graph.commentFrameMap);

		Object.keys(stickersInGraph).forEach((key) => {
			jsonData.stickers[key] = stickersInGraph[key].serializeCommentFrame();
		});
		const fileIds = getArrayOfFilesIdsInProject(jsonData);
		return { jsonData, fileIds };
	} catch (e) {
		console.log(e);
		EventAnalytics.sendErrorInfo("collect-workspace-data-fc-error", {
			message: getPrivateMessageFromError(e) || "collect-workspace-data-fc-error"
		});
		throw e;
	}
}

export async function saveWorkspace(
	callback: VoidFunction = () => {},
	skipShowSavedModal = false,
	cancelCallback = () => {}
) {
	const canSave = getIsSaveFunctionalityAvailable(getUserStore());
	if (!canSave) {
		openPricingPage({ source: "saveWorkspaceFc" });
		cancelCallback?.();
		return;
	}
	EventAnalytics.saveWorkspace();
	try {
		const { activeProjectId } = getWorkspacesStore();

		if (!isSomeWorkspaceOppend()) {
			setIsSaveNewProjectVisible({
				isVisible: true,
				callback,
				cancelCallback
			});
			return;
		}

		const { jsonData, fileIds } = await collectWorkspaceData();

		hideAllModals();

		await WorkspaceApi.putWorkspaceWithFileMove(
			{
				config: jsonData,
				uploaded_file_ids: fileIds
			},
			activeProjectId!
		);
		if (!skipShowSavedModal) {
			showSavedModal();
		}

		Editor.context.graph.toggleIsSavedState(true);
		if (typeof callback === "function") {
			callback();
		}
	} catch (e) {
		console.log(e);
		EventAnalytics.sendErrorInfo("error-save-wrokspace", {
			message: getPrivateMessageFromError(e) || "error-save-wrokspace"
		});
		showToastMessage({
			type: ToastMessageTypes.Error,
			title: "Error while saving workspace, check your internet connection",
			timeout: 5000
		});
		throw e;
	}
}

export function showSavedModal() {
	hideAllModals();
	const modalProjectSaved = document.querySelector("#modal-project-saved");
	if (!modalProjectSaved) return;
	modalProjectSaved.classList.remove("hidden");
	$modalView(modalProjectSaved, false, true);
	setTimeout(() => {
		hideAllModals();
	}, 2500);
}

/**
 *
 * @param {PromiseSettledResult<any>[]} filesResults
 */
const isFilesLoadingWasSuccessful = (filesResults: PromiseSettledResult<any>[]) => {
	for (const result of filesResults) {
		if (result.status === "fulfilled" && result.value === "error") {
			return false;
		}
		if (result.status === "rejected") {
			return false;
		}
	}
	return true;
};

export const isIslandLoaded = (
	graphAdj: Record<string, IUserTemplateParams[]>,
	nodeToCheck: string,
	loadedIds: Set<string>
): boolean => {
	const stack: string[] = [nodeToCheck];
	const visited = new Set<string | undefined>();

	while (stack.length > 0) {
		const id = stack.pop();
		if (!id || visited.has(id)) continue;
		visited.add(id);
		if (!loadedIds.has(id)) return false;
		for (const node of graphAdj[id]) {
			if (!loadedIds.has(node.uuid!)) return false;
			if (visited.has(node.uuid!)) continue;
			stack.push(node.uuid!);
		}
	}

	return true;
};

export const unblockIsland = (
	nodes: IUserTemplateParams[],
	adj: Record<string, IUserTemplateParams[]>,
	nodeToCheck: string,
	ctx: {
		hideNodeLoader: (id: string) => void;
		getNode: (uuid: string) => GraphNode | undefined;
	} = {
		hideNodeLoader,
		getNode: (uuid) => Editor.context.graph.nodesHashMap.get(uuid)
	}
): boolean => {
	const stack: string[] = [nodeToCheck];
	const visited = new Set<string>();

	while (stack.length > 0) {
		const id = stack.pop();
		if (!id || visited.has(id)) continue;
		visited.add(id);

		ctx.hideNodeLoader(id);

		const nodeData = nodes.find((n) => n.uuid === id);
		if (nodeData) {
			loadNodeConnections(nodeData, ctx);
		}

		for (const neighbor of adj[id]) {
			if (!visited.has(neighbor.uuid!)) {
				stack.push(neighbor.uuid!);
			}
		}
	}

	return true;
};

export const makeAdjacencyList = (
	nodes: IUserTemplateParams[]
): Record<string, IUserTemplateParams[]> => {
	const graphAdj: Record<string, IUserTemplateParams[]> = nodes.reduce(
		(acc, node) => {
			acc[node.uuid!] = [];
			return acc;
		},
		{} as Record<string, IUserTemplateParams[]>
	);
	for (const node of nodes) {
		for (const conn of node.connections || []) {
			graphAdj[node.uuid!].push(nodes.find((n) => n.uuid === conn.to.nodeUuid!)!);
			graphAdj[conn.to.nodeUuid!].push(node);
		}
	}
	return graphAdj;
};

export const loadNodeConnections = (
	userNodeData: IUserTemplateParams,
	ctx: {
		getNode: (uuid: string) => GraphNode | undefined;
	} = {
		getNode: (uuid) => Editor.context.graph.nodesHashMap.get(uuid)
	}
) => {
	if (!userNodeData.connections) return;
	for (const outConnection of userNodeData.connections) {
		const fromGraphNode = ctx.getNode(outConnection.from.nodeUuid);
		const toGraphNode = ctx.getNode(outConnection.to.nodeUuid);

		if (fromGraphNode && toGraphNode) {
			if (outConnection.from.type === "trigger") {
				new Connection(
					toGraphNode.triggerSocketsIn[0],
					fromGraphNode.triggerSocketsOut[0],
					true,
					true,
					undefined,
					true
				);
			} else {
				const fromNodeSocket = fromGraphNode.outputSocketGroup.socketHashMap.get(
					outConnection.from.name
				);
				const toNodeSocket = toGraphNode.inputSocketGroup.socketHashMap.get(
					outConnection.to.name
				);

				if (fromNodeSocket && toNodeSocket) {
					if (
						toNodeSocket.dataType === outConnection.to.type &&
						fromNodeSocket.dataType === outConnection.from.type
					) {
						new Connection(toNodeSocket, fromNodeSocket, true, true, undefined, true);
					}
				}
			}
		}
	}
};

const hideNodeLoader = (nodeId: string) => {
	const nodeToUpdate = Editor.context.graph.nodesHashMap.get(nodeId);

	if (nodeToUpdate) {
		nodeToUpdate.loader.hide();
		nodeToUpdate.setIsModificationEnabled(true);
		nodeToUpdate.setIsIslandLoaded(true);
	}
};

const isNodeInView = (
	node: IUserTemplateParams,
	workspace: IWorkspace,
	params: {
		viewportWidth: number;
		viewportHeight: number;
		left: number;
		top: number;
		bottom: number;
		right: number;
	}
) => {
	try {
		if (!workspace?.editor || !node?.position) return true;
		const zoom = Number.isFinite(workspace.editor.zoomScale) ? workspace.editor.zoomScale : 1;
		const left = workspace.editor.position?.x ?? 0;
		const top = workspace.editor.position?.y ?? 0;

		const screenLeft = params.left ?? 0;
		const screenTop = params.top ?? 0;
		const screenRight = params.right ?? 0;
		const screenBottom = params.bottom ?? 0;

		const graphWidth = params?.viewportWidth || 0;
		const graphHeight = params?.viewportHeight || 0;

		const originShiftX = (graphWidth / 2) * (1 - zoom);
		const originShiftY = (graphHeight / 2) * (1 - zoom);
		const viewLeftGraph = (screenLeft - left - originShiftX) / zoom;
		const viewTopGraph = (screenTop - top - originShiftY) / zoom;
		const viewRightGraph = (screenRight - left - originShiftX) / zoom;
		const viewBottomGraph = (screenBottom - top - originShiftY) / zoom;

		// Границы ноды в координатах графа
		const nodeX = Number(node.position.x) || 0;
		const nodeY = Number(node.position.y) || 0;
		const width = Number.isFinite(node.width) ? Number(node.width) : 350;
		const height = Number.isFinite(node.height) ? Number(node.height) : 250;

		const nodeLeft = nodeX;
		const nodeTop = nodeY;
		const nodeRight = nodeX + width;
		const nodeBottom = nodeY + height;

		return (
			nodeLeft < viewRightGraph &&
			nodeRight > viewLeftGraph &&
			nodeTop < viewBottomGraph &&
			nodeBottom > viewTopGraph
		);
	} catch (_e) {
		return false;
	}
};

const sortNodesBasedOnView = (nodes: IUserTemplateParams[], workspace: IWorkspace) => {
	const nodeInView = [];
	const nodesOutOfView = [];
	const rect = Editor.context.graph.dom.editorSpace.getBoundingClientRect();
	for (const node of nodes) {
		if (
			isNodeInView(node, workspace, {
				viewportHeight: rect.height,
				viewportWidth: rect.width,
				top: rect.top,
				left: rect.left,
				right: rect.right,
				bottom: rect.bottom
			})
		) {
			nodeInView.push(node);
		} else {
			nodesOutOfView.push(node);
		}
	}
	return [...nodeInView, ...nodesOutOfView];
};

/**
 *
 * @param workspaceConfig
 * @param fileIds
 * @param workspaceId
 * @returns
 */
export async function loadWorkspace(
	workspaceConfig: IWorkspace,
	fileIds: number[],
	workspaceId: number
) {
	try {
		Editor.context.graph.toggleIsOpenPending(true);
		const nodes = workspaceConfig.nodes;
		const stickers = workspaceConfig.stickers;
		const editor = workspaceConfig.editor;

		let filesIdsList = getArrayOfFilesIdsInProject({ nodes, stickers, editor });
		if (!filesIdsList?.length) {
			filesIdsList = fileIds;
		}
		const idToLinkMap = getLinksAndMap(filesIdsList);
		// Load editor viewer config

		if (editor) {
			Editor.context.graph.navigation.setPosition(editor.position.x, editor.position.y);
			Editor.context.graph.navigation.setZoomScale(editor.zoomScale);
		}

		console.time("START ADDING NODES");
		const changedGlobalIds: Record<string | number, string> = {
			0: "Import Files"
		};
		for (const nodeUserParams of nodes) {
			if (nodeUserParams.globalId in changedGlobalIds) {
				nodeUserParams.globalId = changedGlobalIds[nodeUserParams.globalId];
			}
			// todo: refactor all globalIds with numbers to string
			const cloudBaseNode = await getDefinitionOrAwait(nodeUserParams.globalId);
			if (cloudBaseNode) {
				const _cloudBaseItem = Object.assign({}, cloudBaseNode);
				_cloudBaseItem.userTemplateParams = nodeUserParams;
				_cloudBaseItem.isModificationEnabled = false;
				try {
					addSavedNode(_cloudBaseItem);
				} catch {}
			}
		}

		console.timeEnd("START ADDING NODES");
		const adj = makeAdjacencyList(nodes);
		const loadedSet = new Set<string>();
		// Load static files of node outputs

		let arrayOfPromises: Promise<"error" | SocketDefinition[] | undefined>[] = [];
		const idToLinkMapRes = await idToLinkMap;
		console.time("START LOADING FILES");
		let hasSomeErrors = false;
		const sortedNodes = sortNodesBasedOnView(nodes, workspaceConfig);
		for (const userNodeData of sortedNodes) {
			if (!userNodeData.uuid) continue;
			const nodeToUpdate = Editor.context.graph.nodesHashMap.get(userNodeData.uuid);

			if (!nodeToUpdate) continue;
			const nodeLevelPromises = [];
			for (const outputSocket of userNodeData.outputSocketGroup) {
				if (outputSocket.type === "text") {
					nodeToUpdate.updateOutputs([outputSocket], false);
					loadedSet.add(userNodeData.uuid!);
				} else {
					if (arrayOfPromises.length > 15) {
						const awaitedPrevFiles = await Promise.allSettled(arrayOfPromises);
						hasSomeErrors =
							hasSomeErrors || !isFilesLoadingWasSuccessful(awaitedPrevFiles);
						arrayOfPromises = [];
					}
					const updatePromise = updateNodeWithFiles(
						nodeToUpdate,
						outputSocket,
						idToLinkMapRes,
						workspaceId
					);
					arrayOfPromises.push(updatePromise);
					nodeLevelPromises.push(updatePromise);
				}
			}
			Promise.all(nodeLevelPromises).then(() => {
				loadedSet.add(userNodeData.uuid!);
				if (isIslandLoaded(adj, userNodeData.uuid!, loadedSet)) {
					unblockIsland(nodes, adj, userNodeData.uuid!);
				}
			});
		}

		// Add template stickers
		if (stickers && Object.keys(stickers).length > 0) {
			const stickersKeys = Object.keys(stickers);
			stickersKeys.forEach((stickerId) => {
				loadCommentFrameAccordType(stickers[stickerId]);
			});
		}

		hasSomeErrors =
			hasSomeErrors ||
			!isFilesLoadingWasSuccessful(await Promise.allSettled(arrayOfPromises));
		console.timeEnd("START LOADING FILES");

		for (const userNodeData of nodes) {
			if (!userNodeData.uuid) continue;
			hideNodeLoader(userNodeData.uuid);
		}
		Editor.context.graph.toggleIsSavedState(true);
		if (hasSomeErrors) {
			showToastMessage({
				type: ToastMessageTypes.Error,
				title: "Error while loading workspace, check your internet connection",
				timeout: 5000
			});
		}
	} catch (e) {
		console.log(e);
		EventAnalytics.sendErrorInfo("load-workspace-fc", {
			message: getPrivateMessageFromError(e) || "load-workspace-fc",
			workspaceId
		});
		throw e;
	} finally {
		Editor.context.graph.toggleIsOpenPending(false);
	}
}

export function prepareSocketDefinitions(
	files: IFileType[],
	optionalInfo: Record<string, any> | undefined = undefined,
	isArray = false
) {
	const outputSocketDefinitions = [];

	for (const file of files) {
		const fileNameExtension = file.name.split(".");
		const fileFormat = fileNameExtension.pop()?.toLowerCase();
		const fileName = fileNameExtension[0];
		const fileType = getAssetAvailableTypeByExtension(fileFormat);
		const value = isArray ? [file] : file;
		const socketData = new SocketDefinition(fileName, fileType, {
			...(optionalInfo || {}),
			value
		});
		socketData.value = value;
		outputSocketDefinitions.push(socketData);
	}

	return outputSocketDefinitions;
}

/**
 *
 * @param node
 * @param outputSocket
 * @param idsMap
 * @param workspaceId
 * @returns
 */
export async function updateNodeWithFiles(
	node: GraphNode,
	outputSocket: IWorkspaceFileSocket,
	idsMap: Record<number, DownloadLinkResponse> = {},
	workspaceId = 0
) {
	const promiseArr = [];
	let ok = true;
	try {
		if (Array.isArray(outputSocket.value) && node.globalId !== "Import Files") {
			for (const val of outputSocket.value) {
				if (val.fileId) {
					const fileId = val.fileId;
					if (fileId in idsMap) {
						promiseArr.push(FileApi.getFileFromAws(fileId, idsMap[fileId]));
						continue;
					}
					promiseArr.push(pullAssetViaAws(val.fileId));
				}
			}
			const resolved = await Promise.allSettled(promiseArr);
			const resolvedArr: IFileType[] = [];
			for (const settledPromise of resolved) {
				if (settledPromise.status === "fulfilled") {
					resolvedArr.push(settledPromise.value);
				} else {
					ok = false;
				}
			}

			outputSocket.value = resolvedArr;
			// TODO: Move this list to external place
			node.updateOutputs([outputSocket], false);
		} else {
			const filesPromises = [];

			if (Array.isArray(outputSocket.value)) {
				for (const value of outputSocket.value) {
					const fileId = value.fileId;
					if (fileId && fileId in idsMap) {
						filesPromises.push(FileApi.getFileFromAws(fileId, idsMap[fileId]));
						continue;
					}
					filesPromises.push(pullAssetViaAws(value.fileId));
				}
			} else {
				const fileId = outputSocket.value.fileId ? outputSocket.value.fileId : undefined;
				if (fileId && fileId in idsMap) {
					filesPromises.push(FileApi.getFileFromAws(fileId, idsMap[fileId]));
				} else {
					filesPromises.push(pullAssetViaAws(fileId));
				}
			}
			const resolvedFiles = await Promise.allSettled(filesPromises);
			const retArr = [];
			for (const settledPromise of resolvedFiles) {
				if (settledPromise.status === "fulfilled") {
					retArr.push(settledPromise.value);
				} else {
					ok = false;
				}
			}

			if (node.globalId === "Import Files") {
				outputSocket.value;
				const outputSocketDefinitions = prepareSocketDefinitions(
					retArr,
					outputSocket.optionalInfo
				);
				node.outputSocketGroup.addSockets(outputSocketDefinitions);
				node.outputSocketGroup.updateMultiSockets();
				node.updateOutputs([], false);
				if (!ok) {
					return "error";
				}
				return outputSocketDefinitions;
			}
			node.updateOutputs(retArr, false);
		}
		if (!ok) {
			return "error";
		}
	} catch (error) {
		let status: number | undefined;
		if (
			typeof error === "object" &&
			error !== null &&
			"response" in error &&
			"status" in (error.response as Record<string, any>)
		) {
			status = (error.response as Record<string, any>).status;
		}
		EventAnalytics.sendErrorInfo("error-update-node-width-files", {
			message: getPrivateMessageFromError(error),
			...(workspaceId ? { workspaceId } : {}),
			...(status ? { statusCode: status } : {})
		});
		return "error";
	}
}
